import { Component, effect, inject, input, OnDestroy, OnInit, output } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';

import { GridApi } from 'ag-grid-community';
import { lastValueFrom, ReplaySubject, timeout } from 'rxjs';

import { BarGvkManager } from '@lvflanders/bar';
import { BaasToastMessageService } from '@lvflanders/bar-ng-ui';

import { BarAgGridGvkState } from './bar-ag-grid-gvk-state.type';

@Component({
    selector: 'bar-ag-grid-gvk',
    templateUrl: './bar-ag-grid-gvk.html',
    imports: [MatIconButton, MatIcon, MatTooltip, MatMenu, MatMenuItem, MatMenuTrigger]
})
export class BarAgGridGvk implements OnInit, OnDestroy {
    readonly #toast = inject(BaasToastMessageService);
    readonly #gvkManager = inject(BarGvkManager);
    /*
     * The grid instance api which we can use to set/get configs
     */
    readonly gridApi = input.required<GridApi>();
    /*
     * The key to use to delete/post/get data from gvk
     * e.g. SCREEN_NAME-GRID_NAME
     * where screen_name is for instance "percelen" and grid_name is "bemestingstabel"
     */
    readonly gridKey = input.required<string>();
    /*
     * If the settings should be automatically applied upon grid OnInit
     */
    readonly autoApply = input(true);
    /*
     * Optionally apply the filters set in AG Grid.
     */
    readonly applyFilters = input(false);
    /*
     * When the initialized method is called we return an event when completed. This way the application can call an auto resize (or ...) if need be.
     */
    readonly initialized = output();

    // NOTE we use a replay subject to remember old events which have already been emitted
    readonly #gridReady$ = new ReplaySubject<void>();
    readonly #firstDataRendered$ = new ReplaySubject<void>();

    // Store references to event listeners for cleanup
    #gridReadyListener?: () => void;
    #firstDataRenderedListener?: () => void;

    constructor() {
        effect(() => {
            if (this.gridApi()) {
                // Clean up any existing listeners first
                this.#cleanupEventListeners();

                // Check if grid is already ready and emit the event if so
                if (this.#isGridReady()) {
                    this.#gridReady$.next();
                }

                // Check if data has been rendered and emit the event if so
                if (this.#hasDataBeenRendered()) {
                    this.#firstDataRendered$.next();
                }

                // Create and store event listeners for cleanup
                this.#gridReadyListener = () => this.#gridReady$.next();
                this.#firstDataRenderedListener = () => this.#firstDataRendered$.next();

                this.gridApi().addEventListener('gridReady', this.#gridReadyListener);
                this.gridApi().addEventListener('firstDataRendered', this.#firstDataRenderedListener);
            }
        });
    }

    ngOnInit(): void {
        if (this.autoApply()) {
            this.apply(true);
        }
    }

    ngOnDestroy(): void {
        this.#cleanupEventListeners();
        this.#gridReady$.complete();
        this.#firstDataRendered$.complete();
    }

    /*
     * Clean up event listeners to prevent memory leaks
     */
    #cleanupEventListeners(): void {
        if (this.gridApi()) {
            if (this.#gridReadyListener) {
                this.gridApi().removeEventListener('gridReady', this.#gridReadyListener);
                this.#gridReadyListener = undefined;
            }
            if (this.#firstDataRenderedListener) {
                this.gridApi().removeEventListener('firstDataRendered', this.#firstDataRenderedListener);
                this.#firstDataRenderedListener = undefined;
            }
        }
    }

    /*
     * Initialize the grid to the default settings
     */
    protected init(): void {
        this.gridApi().resetColumnState();

        if (this.applyFilters()) {
            this.gridApi().setFilterModel(null);
        }

        this.initialized.emit();
    }

    /*
     * Save the current config to gvk
     */
    protected async save(): Promise<void> {
        // get current state
        const state: BarAgGridGvkState = {
            columnState: this.gridApi().getColumnState()
        };

        if (this.applyFilters()) {
            state.filters = this.gridApi().getFilterModel();
        }
        try {
            // store state to gvk with provided key
            await this.#gvkManager.setItem(this.gridKey(), state);

            this.#toast.addMessage({
                text: 'Grid instellingen werden bewaard.',
                type: 'success'
            });
        } catch (error) {
            console.log(error);
            this.#toast.addMessage({
                text: 'Grid instellingen konden niet bewaard worden.',
                type: 'error'
            });
        }
    }

    /*
     * Apply the config saved in gvk
     */
    protected async apply(initialAutoApply = false): Promise<void> {
        try {
            const state = await this.#gvkManager.getItem<BarAgGridGvkState>(this.gridKey());
            if (!state) return;
            // Check if grid is already ready, if not wait for it
            if (!this.#isGridReady()) {
                try {
                    await lastValueFrom(this.#gridReady$.pipe(timeout(3000)));
                } catch (timeoutError) {
                    // If timeout occurs, check once more if grid is ready now
                    if (!this.#isGridReady()) {
                        throw new Error('Grid failed to become ready within timeout period');
                    }
                }
            }

            // apply filters when first data is set, otherwise the filter has no use
            if (initialAutoApply && this.applyFilters()) {
                // wait for first data to be rendered before applying filters
                if (!this.#hasDataBeenRendered()) {
                    try {
                        await lastValueFrom(this.#firstDataRendered$.pipe(timeout(5000)));
                    } catch (timeoutError) {
                        // If timeout occurs, proceed anyway - grid might have 0 rows or data might already be ready
                        console.warn('Timeout waiting for firstDataRendered, proceeding with filter application');
                    }
                }
                if (state.filters) {
                    this.gridApi().setFilterModel(state.filters);
                }
            } else if (!initialAutoApply && this.applyFilters() && state.filters) {
                // when preset apply manually we just set the filters.
                this.gridApi().setFilterModel(state.filters);
            }

            // apply column state to the grid
            this.gridApi().applyColumnState({ state: state.columnState, applyOrder: true });

            if (!initialAutoApply) {
                this.#toast.addMessage({
                    text: 'Grid instellingen werden toegepast.',
                    type: 'success'
                });
            }
        } catch (error) {
            if (!initialAutoApply) {
                this.#toast.addMessage({
                    text: 'Grid instellingen konden niet gevonden worden.',
                    type: 'error'
                });
            }
        }
    }

    /*
     * Check if the grid is ready by checking if it has been initialized
     */
    #isGridReady(): boolean {
        return this.gridApi() != null;
    }

    /*
     * Check if data has been rendered in the grid (even if 0 rows)
     * We check if the grid API is available and if data processing has completed
     */
    #hasDataBeenRendered(): boolean {
        return !!this.gridApi() && this.gridApi().getDisplayedRowCount() >= 0;
    }
}
